import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { baseUrl } from '../Data/service/global.service';

/** Plan labels returned with GET /invoice-subscriptions-v2/status (nested under subscription). */
export interface InvoiceSubscriptionEmbeddedPlan {
  id?: number;
  name_ar?: string;
  name_en?: string;
  name_he?: string;
  description_ar?: string;
  description_en?: string;
  description_he?: string;
}

export interface InvoiceSubscriptionStatus {
  subscription: {
    id: number;
    merchant_id: number;
    plan_id: number;
    start_date: string;
    end_date: string;
    documents_limit: number;
    documents_used: number;
    companies_limit: number;
    companies_used: number;
    /** Analysis tokens (invoice subscription v2 — expense AI) */
    tokens_limit?: number;
    tokens_used?: number;
    status: 'active' | 'expired' | 'cancelled' | string;
    auto_renew: boolean;
    auto_renew_plan_id: number | null;
    /** Present on status payload — use when catalog omits inactive / legacy plans */
    plan?: InvoiceSubscriptionEmbeddedPlan | null;
  } | null;
  is_active: boolean;
  is_expired: boolean;
  days_remaining: number;
  documents_remaining: number;
  companies_remaining: number;
  /** Present when API returns a computed remaining token balance */
  tokens_remaining?: number;
}

export interface InvoiceSubscriptionPlan {
  id: number;
  name_ar: string;
  name_en: string;
  name_he: string;
  description_ar?: string;
  description_en?: string;
  description_he?: string;
  documents_limit: number;
  companies_limit: number;
  duration_days: number;
  /** Catalog may return numeric or decimal string (e.g. `"50.00"`) */
  price: number | string;
  is_active: boolean;
  /** Lower sorts first when present */
  sort_order?: number;
  /** Included AI expense analysis tokens for the base plan */
  analysis_tokens_limit?: number;
}

export interface InvoiceSubscriptionHistoryItem {
  id: number;
  plan_id: number;
  start_date: string;
  end_date: string;
  documents_limit?: number;
  documents_used?: number;
  companies_limit?: number;
  companies_used?: number;
  status: string;
  auto_renew: boolean;
  plan?: {
    name_ar?: string;
    name_en?: string;
    name_he?: string;
  };
}

export interface InvoiceSubscriptionOrder {
  id: number;
  order_id: string;
  type: 'subscription' | 'doc_extension' | 'company_extension' | 'token_extension' | string;
  status: 'pending' | 'completed' | 'failed' | string;
  amount: number;
  plan_id?: number;
  documents_count?: number;
  companies_count?: number;
  created_at: string;
  /** رابط الدفع — متوفر فقط للطلبات المعلّقة */
  payment_url?: string | null;
  /** تاريخ انتهاء رابط الدفع (ISO string) */
  link_expiry_datetime?: string | null;
  /** true إذا انتهت صلاحية الرابط (يحسبه الباك‑إند أو نحسبه بأنفسنا) */
  is_link_expired?: boolean;
}

export interface InvoiceCancelOrderResponse {
  message_ar?: string;
  message_en?: string;
  message_he?: string;
}

export interface InvoiceDocExtensionPlan {
  id: number;
  name_ar: string;
  name_en: string;
  extra_documents: number;
  price: number;
}

export interface InvoiceCompanyExtensionPlan {
  id: number;
  name_ar: string;
  name_en: string;
  extra_companies: number;
  price: number;
}

export interface InvoicePurchaseRequestBase {
  plan_id: number;
  merchant_id: number;
  language?: 'ar' | 'he' | 'en';
  /** Remember card for future charges (doc/company add-ons; token flow forces false in UI) */
  save_card?: boolean;
}

/** POST /invoice-subscriptions-v2/purchase/token-extension */
export type PurchaseTokenExtensionRequest = InvoicePurchaseRequestBase;

/** Authenticated catalog — GET /invoice-subscriptions-v2/plans/token-extensions */
export interface InvoiceTokenExtensionPlan {
  id: number;
  name_ar: string;
  name_en: string;
  name_he: string;
  description_ar?: string;
  description_en?: string;
  description_he?: string;
  extra_tokens?: number;
  documents_count?: number;
  price: number;
  is_active?: boolean;
  sort_order?: number;
}

export interface InvoiceSubscriptionPurchaseRequest extends InvoicePurchaseRequestBase {
  auto_renew?: boolean;
}

export interface DirectChargeResult {
  type: 'direct_charge';
  success: boolean;
  card_last4?: string;
  message_en?: string;
  message_ar?: string;
  message_he?: string;
}

export interface PaymentLinkResult {
  type: 'payment_link';
  payment_url: string;
  order_id: string;
  save_card?: boolean;
  auto_renew?: boolean;
  message_en?: string;
  message_ar?: string;
  message_he?: string;
}

export type InvoicePurchaseResult = DirectChargeResult | PaymentLinkResult;

export interface InvoiceAutoRenewStatus {
  auto_renew: boolean;
  auto_renew_plan_id: number | null;
  can_cancel: boolean;
}

export interface InvoiceAutoRenewToggleResponse {
  id: number;
  auto_renew: boolean;
  auto_renew_plan_id: number | null;
  status: string;
}

export interface InvoiceCancelAutoRenewResponse {
  message_ar?: string;
  message_en?: string;
  message_he?: string;
  auto_renew: boolean;
}

/** GET /invoice-subscriptions-v2/dashboard/stats */
export interface InvoiceDashboardRecentInvoice {
  invoice_id: number;
  document_number: string | null;
  invoice_date: string;
  customer_name: string;
  company_name: string;
  total_amount: number;
  invoice_type: number;
  invoice_type_info: {
    code: number;
    name_ar: string;
    name_en: string;
    name_he: string;
  };
  currency: string;
  created_at: string;
  /** User who created the invoice (new field — request from backend) */
  created_by_user_name?: string | null;
}

/** One month data point for the 6-month invoice trend chart */
export interface InvoiceDashboardMonthlyTrendPoint {
  year: number;
  month: number;
  month_label_ar: string;
  month_label_en: string;
  month_label_he: string;
  count: number;
  total_amount?: number;
}

/** Invoice status breakdown for the doughnut chart */
export interface InvoiceDashboardStatusDistribution {
  confirmed: number;
  cancelled: number;
  saved: number;
  draft: number;
  total: number;
}

export interface DashboardFinancialPeriod {
  start_date: string;
  end_date: string;
  income: number;
  expenses: number;
  profit: number;
  comparison_percent: number | null;
}

export interface DashboardFinancialSummary {
  company_id?: number;
  current_month: DashboardFinancialPeriod;
  previous_month: DashboardFinancialPeriod;
  year_to_date: DashboardFinancialPeriod;
}

/** Stable chart key — fixed buckets (`rent`, …) or `cat_<id>` from expense sub-categories. */
export type DashboardExpenseBucketKey = string;

export interface DashboardExpenseBucketItem {
  key: DashboardExpenseBucketKey;
  label_ar: string;
  label_en: string;
  label_he: string;
  amount: number;
  percentage: number;
  category_ids: number[];
}

export interface DashboardExpensesByType {
  period: string;
  start_date: string;
  end_date: string;
  total_expenses: number;
  items: DashboardExpenseBucketItem[];
}

export interface InvoiceDashboardStats {
  merchant_id: number;
  company_id?: number | null;
  recent_invoices?: InvoiceDashboardRecentInvoice[];
  activity: {
    invoices: {
      total_count: number;
      this_month_count: number;
      today_count: number;
      last_30_days_count: number;
      total_invoiced_amount: number;
      average_invoice_amount: number;
      /** Amount invoiced in the current calendar month */
      this_month_total_amount?: number;
      /** Invoice count in the previous calendar month (new field) */
      last_month_count?: number;
      /** Amount invoiced in the previous calendar month */
      last_month_total_amount?: number;
      /** Amount invoiced in the current calendar year */
      this_year_total_amount?: number;
      /** Count of invoices in the current calendar year */
      this_year_count?: number;
      /** Growth %: current month vs previous month (positive = growth, negative = decline) */
      current_month_growth_percent?: number | null;
      /** Growth %: previous month vs the month before it */
      prev_month_growth_percent?: number | null;
      /** Growth %: current year vs same period last year */
      current_year_growth_percent?: number | null;
      /** Last 6 months trend data for the line chart */
      monthly_trend?: InvoiceDashboardMonthlyTrendPoint[];
    };
    customers: {
      total_count: number;
      active_count: number;
      new_last_30_days: number;
    };
    companies: {
      total_count: number;
      active_count: number;
      new_last_30_days: number;
    };
  };
  subscription: {
    is_active: boolean;
    is_expired: boolean;
    days_remaining: number;
    details: {
      subscription_id: number;
      status: string;
      start_date: string;
      end_date: string;
      auto_renew: boolean;
      auto_renew_plan_id: number | null;
      plan: {
        id: number;
        name_en: string;
        name_ar: string;
        name_he?: string | null;
        price: number;
        duration_days: number;
      };
    } | null;
  };
  documents: {
    limit: number;
    used: number;
    remaining: number;
    usage_percent: number;
  };
  payments: {
    completed_count: number;
    pending_count: number;
    failed_count: number;
    total_paid_amount: number;
    latest_completed_payment?: {
      order_id: string;
      type: string;
      amount: number;
      created_at: string;
    } | null;
    latest_pending_payment_link?: {
      order_id: string;
      type: string;
      amount: number;
      payment_url: string;
      created_at: string;
    } | null;
  };
  /** Invoice status distribution for the doughnut chart (new field) */
  invoice_status_distribution?: InvoiceDashboardStatusDistribution;
  /** Expense AI analysis tokens (GET /dashboard/stats) */
  analysis_tokens?: {
    limit: number;
    used: number;
    remaining: number;
    usage_percent: number;
  };
  /** Financial P&L summary (current month, previous month, YTD) */
  financial_summary?: DashboardFinancialSummary | null;
  /** Expense breakdown by dashboard bucket for current month */
  expenses_by_type?: DashboardExpensesByType | null;
}

/** Public landing — GET /public/invoice-subscriptions-v2/plans (no auth) */
export interface PublicPlansListResponse<T> {
  status: number;
  count: number;
  plans: T[];
}

export interface PublicSubscriptionPlanItem {
  id: number;
  name_en: string;
  name_ar: string;
  name_he: string;
  description_en?: string;
  description_ar?: string;
  description_he?: string;
  documents_limit: number;
  companies_limit: number;
  duration_days: number;
  price: number;
  analysis_tokens_limit?: number;
}

export interface PublicDocExtensionPlanItem {
  id: number;
  name_en: string;
  name_ar: string;
  name_he: string;
  description_en?: string;
  description_ar?: string;
  description_he?: string;
  extra_documents: number;
  price: number;
}

export interface PublicCompanyExtensionPlanItem {
  id: number;
  name_en: string;
  name_ar: string;
  name_he: string;
  description_en?: string;
  description_ar?: string;
  description_he?: string;
  extra_companies: number;
  price: number;
}

/**
 * Public landing — GET /public/invoice-subscriptions-v2/plans/token-extensions
 * (add-on packs that increase tokens_limit on the active subscription)
 */
export interface PublicTokenExtensionPlanItem {
  id: number;
  name_en: string;
  name_ar: string;
  name_he: string;
  description_en?: string;
  description_ar?: string;
  description_he?: string;
  /** Tokens added to the subscription when purchased */
  extra_tokens?: number;
  /** Legacy field name if backend still returns it */
  documents_count?: number;
  price: number;
}

/** @deprecated Use PublicTokenExtensionPlanItem */
export type PublicExpenseQuotaPlanItem = PublicTokenExtensionPlanItem;

/** GET /invoice-subscriptions-v2/dashboard/current-plan */
export interface InvoiceDashboardCurrentPlan {
  merchant_id: number;
  has_active_subscription: boolean;
  message_en?: string;
  message_ar?: string;
  message_he?: string;
  plan_details: {
    subscription_id: number;
    subscription_status: string;
    start_date: string;
    end_date: string;
    days_remaining: number;
    auto_renew: boolean;
    auto_renew_plan_id: number | null;
    plan: {
      id: number;
      name_en: string;
      name_ar: string;
      name_he?: string | null;
      description_en?: string;
      description_ar?: string;
      description_he?: string | null;
      price: number;
      duration_days: number;
    };
    usage: {
      documents_limit: number;
      documents_used: number;
      documents_remaining: number;
      companies_limit: number;
      companies_used: number;
      companies_remaining: number;
      analysis_tokens_limit?: number;
      analysis_tokens_used?: number;
      analysis_tokens_remaining?: number;
    };
  } | null;
}

export interface CanAddCompanyResponse {
  allowed: boolean;
  current_count: number;
  limit: number;
  remaining: number;
  plan_name: string | null;
  subscription_id: number | null;
}

export interface CanIssueDocumentResponse {
  allowed: boolean;
  documents_used: number;
  limit: number;
  remaining: number;
  plan_name: string | null;
  subscription_id: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class InvoiceSubscriptionsV2Service {
  private readonly base = `${baseUrl}invoice-subscriptions-v2`;
  private readonly dashboardBase = `${baseUrl}invoice-subscriptions-v2/dashboard`;
  /** No token required — for landing /plans */
  private readonly publicPlansBase = `${baseUrl}public/invoice-subscriptions-v2/plans`;

  constructor(private http: HttpClient) {}

  getPublicSubscriptionPlans(): Observable<PublicPlansListResponse<PublicSubscriptionPlanItem>> {
    return this.http.get<PublicPlansListResponse<PublicSubscriptionPlanItem>>(this.publicPlansBase);
  }

  getPublicDocExtensionPlans(): Observable<PublicPlansListResponse<PublicDocExtensionPlanItem>> {
    return this.http.get<PublicPlansListResponse<PublicDocExtensionPlanItem>>(
      `${this.publicPlansBase}/doc-extensions`,
    );
  }

  getPublicCompanyExtensionPlans(): Observable<PublicPlansListResponse<PublicCompanyExtensionPlanItem>> {
    return this.http.get<PublicPlansListResponse<PublicCompanyExtensionPlanItem>>(
      `${this.publicPlansBase}/company-extensions`,
    );
  }

  getPublicTokenExtensionPlans(): Observable<PublicPlansListResponse<PublicTokenExtensionPlanItem>> {
    return this.http.get<PublicPlansListResponse<PublicTokenExtensionPlanItem>>(
      `${this.publicPlansBase}/token-extensions`,
    );
  }

  /** @deprecated Use getPublicTokenExtensionPlans */
  getPublicExpenseQuotaPlans(): Observable<PublicPlansListResponse<PublicTokenExtensionPlanItem>> {
    return this.getPublicTokenExtensionPlans();
  }

  getDashboardStats(invoiceType?: number | null, companyId?: number | null): Observable<InvoiceDashboardStats> {
    let params = new HttpParams();
    if (invoiceType != null) {
      params = params.set('invoice_type', invoiceType.toString());
    }
    if (companyId != null) {
      params = params.set('company_id', companyId.toString());
    }
    return this.http.get<InvoiceDashboardStats>(`${this.dashboardBase}/stats`, { params });
  }

  getDashboardCurrentPlan(): Observable<InvoiceDashboardCurrentPlan> {
    return this.http.get<InvoiceDashboardCurrentPlan>(`${this.dashboardBase}/current-plan`);
  }

  getStatus(): Observable<InvoiceSubscriptionStatus> {
    return this.http.get<InvoiceSubscriptionStatus>(`${this.base}/status`);
  }

  getHistory(): Observable<InvoiceSubscriptionHistoryItem[]> {
    return this.http.get<InvoiceSubscriptionHistoryItem[]>(`${this.base}/history`);
  }

  getOrders(): Observable<InvoiceSubscriptionOrder[]> {
    return this.http.get<InvoiceSubscriptionOrder[]>(`${this.base}/orders`);
  }

  cancelOrder(orderId: number): Observable<InvoiceCancelOrderResponse> {
    return this.http.delete<InvoiceCancelOrderResponse>(`${this.base}/orders/${orderId}`);
  }

  getPlans(): Observable<InvoiceSubscriptionPlan[]> {
    return this.http.get<InvoiceSubscriptionPlan[]>(`${this.base}/plans`);
  }

  getDocExtensions(): Observable<InvoiceDocExtensionPlan[]> {
    return this.http.get<InvoiceDocExtensionPlan[]>(`${this.base}/plans/doc-extensions`);
  }

  getCompanyExtensions(): Observable<InvoiceCompanyExtensionPlan[]> {
    return this.http.get<InvoiceCompanyExtensionPlan[]>(`${this.base}/plans/company-extensions`);
  }

  getTokenExtensionPlans(): Observable<InvoiceTokenExtensionPlan[]> {
    return this.http.get<InvoiceTokenExtensionPlan[]>(`${this.base}/plans/token-extensions`);
  }

  purchaseSubscription(body: InvoiceSubscriptionPurchaseRequest): Observable<InvoicePurchaseResult> {
    return this.http.post<InvoicePurchaseResult>(`${this.base}/purchase`, body);
  }

  purchaseDocExtension(body: InvoicePurchaseRequestBase): Observable<InvoicePurchaseResult> {
    return this.http.post<InvoicePurchaseResult>(`${this.base}/purchase/doc-extension`, body);
  }

  purchaseCompanyExtension(body: InvoicePurchaseRequestBase): Observable<InvoicePurchaseResult> {
    return this.http.post<InvoicePurchaseResult>(`${this.base}/purchase/company-extension`, body);
  }

  purchaseTokenExtension(body: PurchaseTokenExtensionRequest): Observable<InvoicePurchaseResult> {
    return this.http.post<InvoicePurchaseResult>(`${this.base}/purchase/token-extension`, body);
  }

  getAutoRenew(): Observable<InvoiceAutoRenewStatus> {
    return this.http.get<InvoiceAutoRenewStatus>(`${this.base}/auto-renew`);
  }

  enableAutoRenew(auto_renew_plan_id: number): Observable<InvoiceAutoRenewToggleResponse> {
    return this.http.post<InvoiceAutoRenewToggleResponse>(`${this.base}/auto-renew`, { auto_renew_plan_id });
  }

  cancelAutoRenew(): Observable<InvoiceCancelAutoRenewResponse> {
    return this.http.delete<InvoiceCancelAutoRenewResponse>(`${this.base}/auto-renew`);
  }

  canAddCompany(): Observable<CanAddCompanyResponse> {
    return this.http.get<CanAddCompanyResponse>(`${this.base}/can-add-company`);
  }

  canIssueDocument(): Observable<CanIssueDocumentResponse> {
    return this.http.get<CanIssueDocumentResponse>(`${this.base}/can-issue-document`);
  }
}

